/**
 * Notification Service
 * AIDLC Administrative Backend
 *
 * API calls for the in-app notification system.
 */

import apiClient from './api';
import type { AppNotification } from '../types/backlog';

const PATH = '/notifications';

export async function getNotifications(unreadOnly = false): Promise<AppNotification[]> {
  const params = unreadOnly ? { unread: 'true' } : {};
  const response = await apiClient.get<AppNotification[]>(PATH, { params });
  return response.data;
}

export async function getUnreadCount(): Promise<number> {
  const response = await apiClient.get<{ count: number }>(`${PATH}/unread`);
  return response.data.count;
}

export async function markNotificationRead(id: string): Promise<AppNotification> {
  const response = await apiClient.put<AppNotification>(`${PATH}/${id}/read`);
  return response.data;
}

export async function markAllRead(): Promise<{ count: number }> {
  const response = await apiClient.put<{ ok: boolean; count: number }>(`${PATH}/read-all`);
  return response.data;
}

// ---------------------
// Notification Channel Settings
// ---------------------

const SETTINGS_PATH = '/notification-settings';

/** Email has no settings: the portal sends only as agents@ldaja.com (KD-1 E4). */
export interface NotificationChannelConfig {
  acsConnectionString: string;
  acsPhoneNumber: string;
  vapidPublicKey: string;
  vapidPrivateKey: string;
  vapidSubject: string;
}

export interface ChannelStatus {
  email: boolean;
  sms: boolean;
  push: boolean;
}

export async function getNotificationChannelConfig(): Promise<NotificationChannelConfig> {
  const response = await apiClient.get<NotificationChannelConfig>(`${SETTINGS_PATH}/config`);
  return response.data;
}

export async function updateNotificationChannelConfig(config: Partial<NotificationChannelConfig>): Promise<{ ok: boolean; channelStatus: ChannelStatus }> {
  const response = await apiClient.put<{ ok: boolean; channelStatus: ChannelStatus }>(`${SETTINGS_PATH}/config`, config);
  return response.data;
}

export async function getNotificationChannelStatus(): Promise<ChannelStatus> {
  const response = await apiClient.get<ChannelStatus>(`${SETTINGS_PATH}/status`);
  return response.data;
}

export async function generateVapidKeys(): Promise<{ publicKey: string; privateKey: string }> {
  const response = await apiClient.post<{ publicKey: string; privateKey: string }>(`${SETTINGS_PATH}/generate-vapid`);
  return response.data;
}

export async function sendTestNotification(channel: 'email' | 'sms' | 'push', to?: string): Promise<{ ok: boolean; channel: string; message?: string }> {
  const response = await apiClient.post<{ ok: boolean; channel: string; message?: string }>(`${SETTINGS_PATH}/test/${channel}`, to ? { to } : {});
  return response.data;
}

export interface NotificationPreferences {
  email: boolean;
  sms: boolean;
  push: boolean;
  emailAddress: string | null;
  phoneNumber: string | null;
}

export async function getNotificationPreferences(): Promise<NotificationPreferences> {
  const response = await apiClient.get<NotificationPreferences>(`${PATH}/preferences`);
  return response.data;
}

export async function updateNotificationPreferences(prefs: Partial<NotificationPreferences>): Promise<NotificationPreferences> {
  const response = await apiClient.put<NotificationPreferences>(`${PATH}/preferences`, prefs);
  return response.data;
}
